export const LAB_IMPORT_NONDETECT_RESULT_TOKENS = ["ND", "NT", "U", "NON-DETECT", "NONDETECT"] as const;

export const LAB_IMPORT_NONDETECT_FLAG_TOKENS = [...LAB_IMPORT_NONDETECT_RESULT_TOKENS, "Y"] as const;

const DEFAULT_RESULT_TOKEN_SET = new Set<string>(LAB_IMPORT_NONDETECT_RESULT_TOKENS);
const DEFAULT_FLAG_TOKEN_SET = new Set<string>(LAB_IMPORT_NONDETECT_FLAG_TOKENS);

const normalizeToken = (value: string | null | undefined): string => value?.trim().toUpperCase() ?? "";

const isLessThanExpression = (value: string): boolean =>
  value.startsWith("<") && value.slice(1).trim().length > 0;

const matchesResultToken = (
  value: string,
  nondetectResultValues: ReadonlyArray<string> | undefined,
): boolean =>
  nondetectResultValues
    ? nondetectResultValues.some((candidate) => normalizeToken(candidate) === value)
    : DEFAULT_RESULT_TOKEN_SET.has(value);

export type LabImportNondetectInput = {
  inferFromResultValue?: boolean;
  nondetectFlag: string | null | undefined;
  nondetectResultValues?: ReadonlyArray<string>;
  resultValue: string | null | undefined;
};

/**
 * Classifies the lab row's explicit nondetect flag before considering its Result value.
 * A recognized nonblank flag means nondetect; any other nonblank flag explicitly means detected.
 * Blank or unmapped flags fall back to standard Result tokens and less-than expressions.
 */
export const isLabImportNondetect = ({
  inferFromResultValue = true,
  nondetectFlag,
  nondetectResultValues,
  resultValue,
}: LabImportNondetectInput): boolean => {
  const normalizedFlag = normalizeToken(nondetectFlag);
  if (normalizedFlag !== "") {
    return DEFAULT_FLAG_TOKEN_SET.has(normalizedFlag);
  }

  if (!inferFromResultValue) {
    return false;
  }

  const normalizedResult = normalizeToken(resultValue);
  return (
    matchesResultToken(normalizedResult, nondetectResultValues) || isLessThanExpression(normalizedResult)
  );
};
